"use client";

import { useCallback, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import InstallmentMessage from "./InstallmentMessage";
import PriceWithInstallments from "./PriceWithInstallments";

interface DressPurchasePricePanelProps {
  amount: number;
  currency?: string | null;
  label?: string;
  showFreeShipping?: boolean;
  showPaymentPlans?: boolean;
  className?: string;
  priceClassName?: string;
}

export default function DressPurchasePricePanel({
  amount,
  currency,
  label = "Total price",
  showFreeShipping = true,
  showPaymentPlans = true,
  className,
  priceClassName,
}: DressPurchasePricePanelProps) {
  const [hasVisiblePaymentPlans, setHasVisiblePaymentPlans] = useState(false);
  const handleVisiblePaymentPlans = useCallback(() => {
    setHasVisiblePaymentPlans(true);
  }, []);

  return (
    <div
      className={cn(
        "rounded-xl border border-border/70 bg-background/70 p-4 shadow-sm",
        className
      )}
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <PriceWithInstallments
            amount={amount}
            currency={currency}
            label={label}
            labelPosition="above"
            size="xl"
            align="left"
            showInstallments={false}
            priceClassName={priceClassName}
          />
          {showFreeShipping && (
            <Badge variant="success" size="sm" className="mt-1">
              Free shipping
            </Badge>
          )}
        </div>

        {showPaymentPlans && (
          <div className="rounded-lg border border-border/70 bg-zinc-100 px-3 py-2.5 text-foreground dark:border-white/10 dark:bg-zinc-950 dark:text-white">
            {!hasVisiblePaymentPlans && (
              <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
                Payment plans available
              </p>
            )}
            <InstallmentMessage
              amount={amount}
              currency={currency}
              chip={false}
              logoColor="black"
              textColor="dark"
              onVisibleMessage={handleVisiblePaymentPlans}
              className="mt-1 text-xs leading-5 !text-black dark:!hidden sm:text-xs"
            />
            <InstallmentMessage
              amount={amount}
              currency={currency}
              chip={false}
              logoColor="white"
              textColor="light"
              onVisibleMessage={handleVisiblePaymentPlans}
              className="mt-1 !hidden text-xs leading-5 !text-white dark:!block sm:text-xs"
            />
          </div>
        )}
      </div>
    </div>
  );
}
